'use client';

import SearchFiltersSkeleton from '@dealerportal/ui-components/skeletons/SearchFiltersSkeleton';
import classNames from 'classnames';
import { type MutableRefObject, useEffect, useRef } from 'react';
import { useCatalogContext } from '../../Contexts/CatalogContext';
import { FilterMenu } from './FilterMenu';

type Props = {
  isMobileDevice: boolean;
};

/**
 * Dynamically adjusts the height of the filtermenu container on scroll
 * in order to keep the apply and clear buttons on screen
 */
function scrollFilterMenuButtons(
  filterMenuStickyContainerRef: MutableRefObject<HTMLDivElement | null>,
  filterMenuStickyContainerBottomSentinelRef: MutableRefObject<HTMLDivElement | null>
) {
  if (
    filterMenuStickyContainerRef.current == null ||
    filterMenuStickyContainerBottomSentinelRef == null
  )
    return;
  const top =
    filterMenuStickyContainerRef?.current?.getBoundingClientRect().top ?? 0;
  const bottom =
    filterMenuStickyContainerBottomSentinelRef?.current?.getBoundingClientRect()
      .bottom ?? 0;
  // Only update height every 5px change
  const newHeight = Math.floor((bottom - top) / 5) * 5;
  const newHeightStr = `${newHeight}px`;
  // Only do a DOM manipulation if the height changes for performance
  if (newHeightStr === filterMenuStickyContainerRef.current.style.height)
    return;
  filterMenuStickyContainerRef.current.style.height = newHeightStr;
}

export const FilterPanel = ({ isMobileDevice }: Props) => {
  const filterMenuStickyContainerRef: MutableRefObject<HTMLDivElement | null> =
    useRef(null);
  const filterMenuStickyContainerBottomSentinelRef: MutableRefObject<HTMLDivElement | null> =
    useRef(null);

  const {
    state: { filters, needsHydration, loading },
    filtersVisible,
    setFiltersVisible,
    dispatch,
  } = useCatalogContext();

  // Initialize first load state
  useEffect(() => {
    // Enable filtermenu buttons moving with viewport on desktop
    if (!isMobileDevice) {
      requestIdleCallback(() => {
        const scrollStep = () => {
          scrollFilterMenuButtons(
            filterMenuStickyContainerRef,
            filterMenuStickyContainerBottomSentinelRef
          );
          requestAnimationFrame(scrollStep);
        };
        scrollStep();
      });
    }
  }, [isMobileDevice]);

  return (
    <>
      {/* Desktop filter menu */}
      {!isMobileDevice &&
        (loading ? (
          <SearchFiltersSkeleton />
        ) : (
          <div
            className={classNames(
              'ui-sticky ui-z-[9999999999] md:ui-z-0 ui-flex ui-flex-col ui-top-2 ui-bg-white ui-rounded srp__col-right --desktop',
              { 'ui-hidden': !filtersVisible }
            )}
          >
            <div
              id="filtermenu-sticky-container"
              className="ui-sticky ui-top-0"
              style={{ transition: 'height 0.1s ease' }}
              ref={filterMenuStickyContainerRef}
            >
              <FilterMenu
                filters={filters}
                dispatch={dispatch}
                needsHydration={needsHydration ?? false}
                isMobileDevice={isMobileDevice}
                closeMenu={() => {
                  /* do nothing */
                }}
              />
            </div>
            {/* Flex item to take up empty space for bottom sentinal */}
            <div className="ui-flex-1" />
            {/* Sentinel sticky value that we use to calculate the height of the filter menu within the viewport */}
            <div
              id="filtermenu-bottom-sentinel"
              className="ui-sticky ui-bottom-0"
              ref={filterMenuStickyContainerBottomSentinelRef}
            />
          </div>
        ))}
      {/* Mobile filter menu */}
      {isMobileDevice && (
        <div
          className={classNames(
            'srp__col-right --mobile ui-fixed ui-z-[9999999999] ui-inset-0 ui-hidden ui-bg-white',
            { '!ui-block': filtersVisible }
          )}
        >
          <FilterMenu
            needsHydration={needsHydration ?? false}
            dispatch={dispatch}
            filters={filters}
            isMobileDevice={isMobileDevice}
            closeMenu={() => setFiltersVisible(false)}
          />
        </div>
      )}
    </>
  );
};
